/**
 * Funnel card — stage-by-stage bar chart driven by metrics.fct_funnel_daily.
 *
 * Empty-data tolerance: the backend `.catch`es each query and returns []
 * rows when metrics.fct_funnel_daily is pre-launch-empty. We render a
 * placeholder copy rather than a chart with zero bars.
 */
import { formatNumber } from '@/lib/utils';

interface Stage {
  stage: string;
  count: number;
}
interface BiggestDrop {
  from: string;
  to: string;
  drop_pct: number;
}
interface FunnelData {
  timeframe: string;
  funnel: Stage[];
  biggest_drop: BiggestDrop;
}

export function FunnelCard({ data }: { data: FunnelData }) {
  const rows = data.funnel ?? [];
  const top = rows[0]?.count || 1;

  return (
    <section className="bg-card border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-1">Funnel</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Stages aggregated over the selected timeframe ({data.timeframe}).
      </p>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No funnel data yet — populates as users move through onboarding (source:
          metrics.fct_funnel_daily).
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => {
              const pct = top > 0 ? (r.count / top) * 100 : 0;
              return (
                <tr key={r.stage} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium w-[35%]">{r.stage}</td>
                  <td className="py-2 pr-3">
                    <div className="bg-muted rounded h-3 relative overflow-hidden">
                      <div
                        className="bg-primary h-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right w-[20%] font-mono">
                    {formatNumber(r.count)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {data.biggest_drop && data.biggest_drop.drop_pct > 0 && (
        <p className="text-sm text-amber-700 mt-3">
          Biggest drop-off: <strong>{data.biggest_drop.from}</strong> →{' '}
          <strong>{data.biggest_drop.to}</strong> ({data.biggest_drop.drop_pct.toFixed(0)}% leak)
        </p>
      )}
    </section>
  );
}
